/** /tour-operators
 *
 * Server-side pagination of root operators (parent_id=null). Franchise
 * parents expand inline to load their licensees on demand. The reusable
 * QuerySearch widget (same `?q=` DSL / `?filters=` AST as Bookings /
 * Notifications) drives filtering; while a filter is active — OR the sort is
 * non-default — the parent_id restriction is lifted and inline expansion is
 * disabled so all matching TOs appear in one flat, server-sorted list.
 *
 * Closed tenants (tenant retirement: lifecycle not `active`) are left out by
 * the backend unless asked: **Show closed** (`?closed=1`) sends
 * `include_closed=1` — on the roots, the licensees and a batch's
 * `all_matching` alike — and a closed row carries the Closed chip. A
 * `lifecycle=` search lists them too.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Switch,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorefrontIcon from '@mui/icons-material/Storefront';
import StoreIcon from '@mui/icons-material/Store';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import {
  DataGridPro,
  gridColumnFieldsSelector,
  useGridApiRef,
  type GridColDef,
  type GridPaginationModel,
  type GridRowSelectionModel,
  type GridSlots,
  type GridToolbarProps,
} from '@mui/x-data-grid-pro';
import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';

import * as topsApi from '@/api/tourOperators';
import type { TourOperatorListItem } from '@/api/tourOperators';
import type { BatchSelection } from '@/api/batch';
import { ConsoleApiError, isStatementTimeout } from '@/api/client';
import type { BatchActionDef, BatchRefusals } from '@/components/DataGrid/batchTypes';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import { useBatchAction } from '@/components/DataGrid/useBatchAction';
import { GridControlsToolbar } from '@/components/DataGrid/GridControlsToolbar';
import { gridRefreshToolbar, useGridAutoRefresh } from '@/components/DataGrid/useGridAutoRefresh';
import { SearchTimeoutAlert } from '@/components/DataGrid/SearchTimeoutAlert';
import { PageHeader } from '@/components/PageHeader';
import {
  QuerySearchInput,
  parse,
  serialize,
  toWireAst,
  type FilterNode,
} from '@/components/QuerySearch';
import { useGridPresets, type PresetFiltersAdapter } from '@/hooks/useGridPresets';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { useSnackbar } from '@/components/Snackbar';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { formatRelativeTime } from '@/utils/format';

import { batchEditAction } from '@/features/batch-edit/batchEditAction';

import { LifecycleChip } from './lifecycle/LifecycleChip';
import { EnvironmentBadge } from './sandbox/EnvironmentBadge';
import { buildTourOperatorNamespaces } from './tourOperatorsNamespaces';

const DEFAULT_PAGE_SIZE = 50;

/** Column field order (mirrors the columns memo — keep in sync). `__expand__`
 *  carries the franchise expand affordance and stays a normal column. */
const FIELD_ORDER = [
  '__expand__',
  'name',
  'code',
  'kind',
  'num_children',
  'email',
  'url',
  'created_ts',
];

/** Presets store the filter state as the raw `?q=` DSL text (canonical form). */
const PRESET_FILTERS: PresetFiltersAdapter = {
  urlParams: ['q'],
  capture: (get) => {
    const q = get('q');
    return q ? { q } : undefined;
  },
  toUrlParams: (filters) => {
    const q = (filters as { q?: unknown } | null)?.q;
    return typeof q === 'string' && q ? { q } : ({} as Record<string, string>);
  },
};

export function TourOperatorsListPage() {
  const snackbar = useSnackbar();
  const { hasPerm } = usePermissions();
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  // Creation is a routed wizard now; legacy `?new=1` bookmarks still land there.
  useCreateIntent(() => navigate('/tour-operators/new', { replace: true }));

  // Preset support: filters are the `?q=` DSL; sorting IS wired for the
  // backend-whitelisted columns (name / code / created_ts). A non-default sort
  // OR an active filter flattens the view (below) — sorting a mix of parents +
  // their licensees is meaningless, and a filter should span the whole catalog.
  const presets = useGridPresets({
    grid: 'tour_operators',
    defaultOrder: FIELD_ORDER,
    supportsSort: true,
    defaultOrdering: 'name',
    filters: PRESET_FILTERS,
  });

  // Pagination (0-indexed for MUI)
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [extendedTimeout, setExtendedTimeout] = useState(false);

  // ---- QuerySearch state (URL-driven) --------------------------------------
  const [sp, setSp] = useSearchParams();
  const namespaces = useMemo(() => buildTourOperatorNamespaces(), []);
  const q = presets.getParam('q') ?? '';
  // Closed tenants are left out unless asked (the backend's default).
  const showClosed = sp.get('closed') === '1';
  const setShowClosed = useCallback(
    (on: boolean) => {
      const next = new URLSearchParams(sp);
      if (on) next.set('closed', '1');
      else next.delete('closed');
      setSp(next, { replace: true });
    },
    [sp, setSp],
  );

  const { tree, diagnostics } = useMemo(() => {
    const r = parse(q, namespaces);
    return { tree: r.nodes, diagnostics: r.diagnostics };
  }, [q, namespaces]);
  const wire = useMemo(() => toWireAst(tree), [tree]);

  const { beforeUrlWrite } = presets;
  const setQuery = useCallback(
    (nodes: FilterNode[]) => {
      const next = new URLSearchParams(sp);
      beforeUrlWrite(next);
      const str = serialize(nodes);
      if (str) next.set('q', str);
      else next.delete('q');
      setSp(next, { replace: true });
    },
    [sp, setSp, beforeUrlWrite],
  );

  const lastDiagKey = useRef<string>('');
  useEffect(() => {
    if (diagnostics.length === 0) return;
    const key = `${q}::${diagnostics.length}`;
    if (key === lastDiagKey.current) return;
    lastDiagKey.current = key;
    snackbar.show(
      `Some of your search couldn't be interpreted: ${diagnostics[0]!.message}`,
      'warning',
    );
  }, [diagnostics, q, snackbar]);

  // Inline franchise expansion: the expanded parents. Their licensees are
  // queries (below), so they refresh with the grid — Refresh, auto-refresh and
  // the batch-action invalidation of ['tourOperators'] alike.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const hasFilter = wire.length > 0;
  // A non-default sort flattens the view too — `name` is the default ordering.
  const isDefaultSort = presets.ordering === 'name';
  // Flat mode ⇒ no roots restriction, no inline expansion.
  const isFlat = hasFilter || !isDefaultSort;

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 0 }));
  }, [q, showClosed]);

  // A new search or sort must not silently keep the extended timeout budget.
  useEffect(() => {
    setExtendedTimeout(false);
  }, [q, presets.ordering]);

  // Collapse everything when pagination, search or sort changes.
  useEffect(() => {
    setExpanded(new Set());
  }, [pagination.page, pagination.pageSize, q, presets.ordering, showClosed]);

  // Toolbar Refresh + auto-refresh, shared by the roots query and every
  // expanded parent's licensees. Polling pauses on the extended-timeout tier.
  const autoRefresh = useGridAutoRefresh({ grid: 'tour_operators' });

  // Main query: roots only when browsing, all matching results when flat.
  const query = useQuery({
    queryKey: [
      'tourOperators',
      'list',
      pagination.page,
      pagination.pageSize,
      q,
      presets.ordering,
      extendedTimeout,
      showClosed,
    ],
    queryFn: () =>
      topsApi.list({
        page: pagination.page + 1,
        pageSize: pagination.pageSize,
        ordering: presets.ordering,
        filters: wire,
        parent_id: isFlat ? undefined : 'null',
        ...(showClosed ? { include_closed: true } : {}),
        ...(extendedTimeout ? { timeout: 'extended' as const } : {}),
      }),
    placeholderData: (prev) => prev,
    enabled: !presets.blocking,
    ...autoRefresh.queryOptions(extendedTimeout),
  });

  // Licensees of each expanded parent. `combine` (memoised by TanStack until a
  // result or the id list changes) zips results back to their parent ids.
  const expandedIds = useMemo(() => [...expanded], [expanded]);
  const combineChildren = useCallback(
    (results: UseQueryResult<TourOperatorListItem[]>[]) => {
      const byParent: Record<number, TourOperatorListItem[]> = {};
      const loading = new Set<number>();
      const failed: number[] = [];
      results.forEach((r, i) => {
        const id = expandedIds[i]!;
        if (r.data) byParent[id] = r.data;
        else if (r.isError) failed.push(id);
        else loading.add(id);
      });
      return {
        byParent,
        loading,
        failed,
        refetch: () => results.forEach((r) => void r.refetch({ cancelRefetch: false })),
      };
    },
    [expandedIds],
  );
  const children = useQueries({
    queries: expandedIds.map((id) => ({
      queryKey: ['tourOperators', 'children', id, showClosed],
      queryFn: () =>
        topsApi
          .list({ parent_id: id, pageSize: 500, ordering: 'name', ...(showClosed ? { include_closed: true } : {}) })
          .then((r) => r.results),
      enabled: !isFlat,
      ...autoRefresh.queryOptions(extendedTimeout),
    })),
    combine: combineChildren,
  });
  // A parent whose licensees never loaded folds back up (a failed background
  // refresh keeps the rows it already had).
  useEffect(() => {
    if (children.failed.length === 0) return;
    snackbar.show('Couldn’t load licensees.', 'error');
    setExpanded((prev) => {
      const s = new Set(prev);
      for (const id of children.failed) s.delete(id);
      return s;
    });
  }, [children.failed, snackbar]);

  // ---- batch selection (generic batch-edit action) --------------------------
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [allMatching, setAllMatching] = useState(false);
  const clearSelection = useCallback(() => {
    setSelectionModel([]);
    setAllMatching(false);
  }, []);
  const handleSelectionChange = useCallback((model: GridRowSelectionModel) => {
    setSelectionModel(model);
    setAllMatching((prev) => (prev ? false : prev));
  }, []);
  // Selection is meaningless across a query change.
  useEffect(() => {
    clearSelection();
  }, [q, presets.ordering, showClosed, clearSelection]);

  // Mirror of the LIST query params, so `all_matching` resolves the same set
  // the grid shows (roots only while browsing the tree; everything when flat).
  const batchFilterQuery = useMemo(() => {
    const usp = new URLSearchParams();
    if (wire.length > 0) usp.set('filters', JSON.stringify(wire));
    if (!isFlat) usp.set('parent_id', 'null');
    if (showClosed) usp.set('include_closed', '1');
    return usp.toString();
  }, [wire, isFlat, showClosed]);

  const batchAction = useBatchAction({
    resource: 'tour_operators',
    queryKey: ['tourOperators'],
    onCleared: clearSelection,
  });
  const runStandard = useCallback(
    (
      action: string,
      label: string,
      selection: BatchSelection,
      params?: Record<string, unknown>,
      refusals?: BatchRefusals,
      pinnedQuery?: string,
    ) => batchAction.run({ action, label, selection, params, refusals, filterQuery: pinnedQuery ?? batchFilterQuery }),
    [batchAction, batchFilterQuery],
  );
  // Declared unconditionally; BatchActionsMenu filters by `permission`.
  const batchActions = useMemo<BatchActionDef[]>(
    () => [batchEditAction('tour_operators', 'tour operator')],
    [],
  );

  // Merge root rows with loaded children for display
  const displayRows = useMemo<TourOperatorListItem[]>(() => {
    const roots = query.data?.results ?? [];
    if (isFlat) return roots;
    const result: TourOperatorListItem[] = [];
    for (const root of roots) {
      result.push(root);
      if (expanded.has(root.id)) {
        result.push(...(children.byParent[root.id] ?? []));
      }
    }
    return result;
  }, [query.data, expanded, children.byParent, isFlat]);

  const toggleExpand = useCallback((row: TourOperatorListItem) => {
    setExpanded((prev) => {
      const s = new Set(prev);
      if (s.has(row.id)) s.delete(row.id);
      else s.add(row.id);
      return s;
    });
  }, []);
  const loadingFor = children.loading;

  function copyCode(code: string) {
    void navigator.clipboard.writeText(code).then(
      () => snackbar.show(`Copied "${code}"`, 'success'),
      () => snackbar.show('Copy failed — clipboard unavailable', 'error'),
    );
  }

  const isExpandable = (row: TourOperatorListItem) =>
    !isFlat && row.parent_id == null && (row.is_franchise || row.num_children > 0);

  const columns = useMemo<GridColDef<TourOperatorListItem>[]>(
    () => [
      // Expand toggle — only shown for franchise parents in tree mode
      {
        field: '__expand__',
        headerName: '',
        width: 40,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (p) => {
          if (!isExpandable(p.row)) return null;
          const loading = loadingFor.has(p.row.id);
          const open = expanded.has(p.row.id);
          return (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(p.row);
              }}
              aria-label={open ? 'Collapse licensees' : 'Expand licensees'}
            >
              {loading ? (
                <CircularProgress size={14} />
              ) : open ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          );
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1.5,
        minWidth: 180,
        renderCell: (p) => {
          const isChild = p.row.parent_id != null;
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: isChild ? 2 : 0 }}>
              {isChild && (
                <SubdirectoryArrowRightIcon
                  sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }}
                />
              )}
              <Link
                component={RouterLink}
                to={`/tour-operators/${p.row.id}`}
                underline="hover"
                onClick={(e) => e.stopPropagation()}
                sx={{ color: 'text.primary', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {p.row.name}
              </Link>
              {/* A sandbox is a tenant of its own (`<code>sbx`): badged, so it is never taken for
                  its production operator. */}
              <EnvironmentBadge environment={p.row.environment} />
              {/* A closed tenant (listed with Show closed or a lifecycle= search). */}
              <LifecycleChip lifecycle={p.row.lifecycle} />
            </Box>
          );
        },
      },
      {
        field: 'code',
        headerName: 'Code',
        width: 170,
        renderCell: (p) => (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%' }}>
            <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
              {p.row.code}
            </Box>
            <Tooltip title="Copy code" placement="top" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  copyCode(p.row.code);
                }}
                sx={{ ml: 'auto', p: 0.25 }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
      {
        field: 'kind',
        headerName: 'Kind',
        width: 120,
        sortable: false,
        renderCell: (p) => renderKindChip(p.row),
      },
      {
        field: 'num_children',
        headerName: 'Licensees',
        width: 100,
        align: 'right',
        headerAlign: 'right',
        // Annotation (Count), not in the backend ordering_fields — keep unsortable.
        sortable: false,
        renderCell: (p) =>
          p.row.parent_id == null && p.row.num_children > 0 ? p.row.num_children : '—',
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 120,
        sortable: false, // not in the backend ordering_fields
        renderCell: (p) =>
          p.row.email ? (
            <Link
              href={`mailto:${p.row.email}`}
              underline="always"
              onClick={(e) => e.stopPropagation()}
            >
              {p.row.email}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        field: 'url',
        headerName: 'Website',
        flex: 1,
        minWidth: 120,
        sortable: false, // not in the backend ordering_fields
        renderCell: (p) =>
          p.row.url ? (
            <Link
              href={p.row.url}
              target="_blank"
              rel="noopener noreferrer"
              underline="always"
              onClick={(e) => e.stopPropagation()}
            >
              {p.row.url}
            </Link>
          ) : (
            '—'
          ),
      },
      {
        field: 'created_ts',
        headerName: 'Created',
        width: 130,
        renderCell: (p) =>
          p.row.created_ts ? (
            <Tooltip title={p.row.created_ts} placement="top" arrow>
              <Box component="span">{formatRelativeTime(p.row.created_ts)}</Box>
            </Tooltip>
          ) : (
            '—'
          ),
      },
    ],
    // Re-derive columns when expand/loading/flat state changes so icons update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expanded, loadingFor, isFlat, toggleExpand],
  );

  const apiError =
    query.error instanceof ConsoleApiError &&
    query.error.status === 400 &&
    !isStatementTimeout(query.error)
      ? query.error
      : null;
  const backendMessage =
    (apiError?.body as { filters?: string } | undefined)?.filters ?? apiError?.message ?? null;

  const shownOnce = useRef(false);
  useEffect(() => {
    if (backendMessage && !shownOnce.current) {
      shownOnce.current = true;
      snackbar.show(`Search rejected: ${backendMessage}`, 'error');
    }
    if (!backendMessage) shownOnce.current = false;
  }, [backendMessage, snackbar]);

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="top-directory-tree"
        title="Tour operators"
        subtitle="Regular and franchise operators. Click a franchise row to load its licensees."
        actions={
          hasPerm('console_top_manage') ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/tour-operators/new')}>
              Create tour operator
            </Button>
          ) : undefined
        }
      />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2, alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <QuerySearchInput
            namespaces={namespaces}
            value={tree}
            onChange={setQuery}
            placeholder="Search operators…  e.g. kind=franchise & restech=bokun"
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showClosed}
              onChange={(_, on) => setShowClosed(on)}
              inputProps={{ role: 'switch' }}
            />
          }
          label="Show closed"
          sx={{ flex: 'none', mr: 0, whiteSpace: 'nowrap' }}
        />
      </Stack>

      {isStatementTimeout(query.error) && (
        <SearchTimeoutAlert
          extendedAttempted={extendedTimeout}
          onRetryExtended={!extendedTimeout ? () => setExtendedTimeout(true) : undefined}
          retrying={query.isFetching}
        />
      )}
      {backendMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {backendMessage}
        </Alert>
      )}
      {query.isError && !backendMessage && !isStatementTimeout(query.error) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn’t load tour operators: {(query.error as Error).message}
        </Alert>
      )}

      {presets.blocking ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataGridPro<TourOperatorListItem>
          apiRef={apiRef}
          rows={displayRows}
          rowCount={query.data?.count ?? 0}
          getRowId={(r) => r.id}
          columns={orderColumns(columns, presets.orderedFields)}
          loading={query.isLoading || query.isFetching}
          sx={{ flex: 1, minHeight: 360 }}
          paginationMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[25, 50, 100, 200]}
          sortingMode="server"
          sortModel={presets.sortModel}
          onSortModelChange={presets.onSortModelChange}
          disableRowSelectionOnClick
          disableColumnFilter
          checkboxSelection
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleSelectionChange}
          keepNonExistentRowsSelected
          columnVisibilityModel={presets.columnVisibilityModel}
          onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
          onColumnOrderChange={() => presets.onColumnOrderChange(gridColumnFieldsSelector(apiRef))}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                {hasFilter ? 'No tour operators match your search.' : 'No tour operators.'}
              </Box>
            ),
            toolbar: GridControlsToolbar as GridSlots['toolbar'],
          }}
          slotProps={{
            loadingOverlay: { variant: 'linear-progress', noRowsVariant: 'skeleton' },
            toolbar: {
              refresh: gridRefreshToolbar(query, autoRefresh, {
                paused: extendedTimeout,
                onRefresh: () => {
                  void query.refetch({ cancelRefetch: false });
                  children.refetch();
                },
              }),
              hideFilterButton: true,
              exportFileName: 'tour-operators',
              presets: presets.toolbar,
              batch: {
                actions: batchActions,
                ids: selectionModel as Array<number | string>,
                allMatching,
                count: allMatching ? (query.data?.count ?? 0) : selectionModel.length,
                matched: query.data?.count ?? null,
                filterQuery: batchFilterQuery,
                tenantFilterId: null,
                rowTenantIds: [],
                runStandard,
                clearSelection,
                onSelectAllMatching: () => setAllMatching(true),
                isPending: batchAction.isPending,
              },
            } as GridToolbarProps,
          }}
        />
      )}

    </Box>
  );
}

function renderKindChip(row: TourOperatorListItem) {
  if (row.parent_id != null) {
    return <Chip size="small" color="info" icon={<StoreIcon />} label="Licensee" />;
  }
  if (row.is_franchise || row.num_children > 0) {
    return <Chip size="small" color="primary" icon={<AccountTreeIcon />} label="Franchise" />;
  }
  return <Chip size="small" icon={<StorefrontIcon />} label="Regular" />;
}
